import { Button, Dialog, DialogContent, DialogContentText, LinearProgress, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { ConnectionStatus } from '../../../store/types'

type Props = {
    connectionStatus: ConnectionStatus
    alreadyConnected?: boolean
}

// Paths that should not show the reconnection popup
const IGNORED_PATHS = ['/missions']

export const ConnectionFailedOverlay: React.FC<Props> = ({ connectionStatus, alreadyConnected = false }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [refreshButtonEnabled, setRefreshButtonEnabled] = useState(false)
    const [countdown, setCountdown] = useState(0)

    // Check if current path should ignore the popup
    const shouldIgnorePopup = () => {
        const currentPath = window.location.pathname + window.location.search
        return IGNORED_PATHS.some((pathPattern) => currentPath.includes(pathPattern))
    }

    useEffect(() => {
        console.log('[ConnectionFailedOverlay] connectionStatus changed:', connectionStatus, 'alreadyConnected:', alreadyConnected)

        // Don't show reconnection popup if user is already connected from another device
        // Let the AlreadyConnectedDialog handle this case
        if (alreadyConnected) {
            console.log('[ConnectionFailedOverlay] Ignoring popup - user already connected from another device')
            setIsOpen(false)
            return
        }

        // Check if we should ignore the popup on current path
        if (shouldIgnorePopup()) {
            console.log('[ConnectionFailedOverlay] Ignoring popup on current path:', window.location.pathname)
            setIsOpen(false)
            return
        }

        // to prevent this status to be overridden by ConnectionStatus.NOT_INITIALIZED
        if (
            connectionStatus === ConnectionStatus.RECONNECT_FAILED ||
            connectionStatus === ConnectionStatus.CONNECTING
        ) {
            console.log('[ConnectionFailedOverlay] Scheduling dialog to open in 3s')
            // Capture the status now to determine correct countdown later
            const currentStatus = connectionStatus
            // Add 3 second delay before showing popup to allow seamless reconnection
            const delayTimer = setTimeout(() => {
                console.log('[ConnectionFailedOverlay] Opening dialog after delay')
                setIsOpen(true)
                // Disable refresh button and start countdown when dialog opens
                setRefreshButtonEnabled(false)
                setCountdown(currentStatus === ConnectionStatus.CONNECTING ? 10 : 5)
            }, 3000)

            // Cleanup timeout if connection is restored before delay expires
            return () => clearTimeout(delayTimer)
        } else if (
            connectionStatus === ConnectionStatus.CONNECTED ||
            connectionStatus === ConnectionStatus.NOT_INITIALIZED
        ) {
            console.log('[ConnectionFailedOverlay] Closing dialog')
            setIsOpen(false)
            setRefreshButtonEnabled(false)
            setCountdown(0)
        }
    }, [connectionStatus, alreadyConnected])

    // Countdown timer effect
    useEffect(() => {
        if (!isOpen || countdown <= 0) {
            if (countdown === 0 && isOpen) {
                setRefreshButtonEnabled(true)
            }
            return
        }

        const timer = setTimeout(() => {
            setCountdown((prev) => prev - 1)
        }, 1000)

        return () => clearTimeout(timer)
    }, [isOpen, countdown])

    return connectionStatus !== ConnectionStatus.CONNECTED ? (
        <Dialog open={isOpen}>
            <DialogContent
                sx={{
                    maxWidth: '30rem',
                }}
            >
                <DialogContentText fontSize={'1.2rem'}>
                    {connectionStatus === ConnectionStatus.CONNECTING ? (
                        <>
                            <Typography variant="h6" fontWeight="bold" color="secondary" paragraph>
                                Connection interrupted
                            </Typography>
                            <Typography variant="body1" paragraph>
                                We're working on reconnecting you to the server. This usually takes just a few seconds.
                            </Typography>
                            <LinearProgress sx={{ my: 2 }} />
                            <Button
                                onClick={() => window.location.reload()}
                                fullWidth
                                sx={{ mt: 2 }}
                                disabled={!refreshButtonEnabled}
                            >
                                {refreshButtonEnabled ? 'Refresh Page' : 'Please Wait...'}
                            </Button>
                            <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1.5 }}>
                                {!refreshButtonEnabled
                                    ? `Automatic reconnection in progress (${countdown}s)`
                                    : 'Still trying to reconnect. You can refresh if needed.'}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="h6" fontWeight="bold" color="secondary" paragraph>
                                Connection lost
                            </Typography>
                            <Typography variant="body1" paragraph>
                                We couldn't restore the connection automatically. Please check your internet connection
                                and refresh the page to continue.
                            </Typography>
                            <LinearProgress color="error" sx={{ my: 2 }} />
                            <Button
                                onClick={() => window.location.reload()}
                                fullWidth
                                sx={{ mt: 2 }}
                                disabled={!refreshButtonEnabled}
                            >
                                {refreshButtonEnabled ? 'Refresh Page' : 'Please Wait...'}
                            </Button>
                            <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1.5 }}>
                                {!refreshButtonEnabled
                                    ? `You can refresh in ${countdown}s`
                                    : 'Automatic reconnection attempts continue in the background'}
                            </Typography>
                        </>
                    )}
                </DialogContentText>
            </DialogContent>
        </Dialog>
    ) : null
}
